import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { AddOrganizacionMutation } from "@/features/administracion/organizaciones/api/add-organizacion";
import { DeleteOrganizacionMutation } from "@/features/administracion/organizaciones/api/delete-organizacion";
import { EditOrganizacionMutation } from "@/features/administracion/organizaciones/api/edit-organizacion";
import { getAllOrganizaciones, useGetAllOrganizacionesQuery } from "@/features/administracion/organizaciones/api/get-all-organizaciones";
import { GetOrganizacionQuery } from "@/features/administracion/organizaciones/api/get-organizacion";
import { SetOrganizacionHabilitadoMutation } from "@/features/administracion/organizaciones/api/set-organizacion-habilitado";
import OrganizacionForm, { organizacionFormSchema } from "@/features/administracion/organizaciones/components/organizacion-form";
import ParamsManager from "@/features/configuration/components/params-manager";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialogObs from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export default function OrganizacionesPage() {
    const openModal = useOpenModal();

    const filterData = useFilters({
        filters: [
            { type: "string", name: "nombre", label: "Nombre" },
            { type: "string", name: "numDocumento", label: "N° Documento" },
            { type: "string", name: "email", label: "Email" },
            { type: "string", name: "telefono", label: "Teléfono" },
            { type: "pagination" }
        ]
    })

    const fetchData = useGetAllOrganizacionesQuery(filterData.filterData);

    const columnData = useColumns({
        key: "organizaciones",
        columns: [
            { key: "documento", header: "N° Documento", render: row => formatString(row.numDocumento) },
            { key: "nombre", header: "Nombre", render: row => formatString(row.nombre) },
            { key: "email", header: "Email", render: row => formatString(row.email) },
            { key: "telefono", header: "Teléfono", render: row => formatString(row.telefono) },
            { key: "habilitado", header: "Estado", render: row => row.habilitado ? "Habilitado" : "Deshabilitado", align: "center" },
            { key: "direccion", header: "Dirección", render: row => formatString(row.direccion), show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ]
    })

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Organizaciones">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>

        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.EditButton
                                disabled={row.protegido}
                                onClick={() => openModal("edit-params", row.id)}
                                title="Parámetros"
                            />
                            <DataTable.EntryOption.Button
                                disabled={row.protegido}
                                Icon={row.habilitado ? BlockIcon : CheckCircleOutlineIcon}
                                title={row.habilitado ? "Deshabilitar" : "Habilitar"}
                                onClick={() => openModal("toggle-habilitado", row)}
                            />
                            <DataTable.EntryOption.DeleteButton
                                disabled={row.protegido}
                                onClick={() => openModal("delete", row)}
                            />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        {/* Modales */}
        <Drawer code="add">
            <ModalHeader>Agregar Organización</ModalHeader>
            <AddOrganizacionMutation>
                {mutation => <SchemaFormHook
                    schema={organizacionFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify(`La organización se ha agregado correctamente.`, { variant: "success" });
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <OrganizacionForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddOrganizacionMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Organización</ModalHeader>
            <ModalValueExtractor>
                {organizacionId => <GetOrganizacionQuery id={organizacionId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {organizacion => <EditOrganizacionMutation id={organizacion.id}>
                            {mutation => <SchemaFormHook
                                schema={organizacionFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify(`La organización se ha editado correctamente.`, { variant: "success" });
                                }}
                                defaultValues={{ ...organizacion }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <OrganizacionForm form={form} margin={3} />}
                            </SchemaFormHook>}
                        </EditOrganizacionMutation>}
                    </DataFetcher>}
                </GetOrganizacionQuery>}
            </ModalValueExtractor>
        </Drawer>

        <Dialog code="edit-params" size="lg">
            <ModalHeader>Editar Parámetros</ModalHeader>
            <ModalValueExtractor>
                {organizacionId => <ParamsManager organizacionId={organizacionId} />}
            </ModalValueExtractor>
        </Dialog>

        <ConfirmDialog code="toggle-habilitado">
            <ModalValueExtractor>
                {organizacion => <SetOrganizacionHabilitadoMutation id={organizacion.id}>
                    {mutation => <ConfirmDialog.Content
                        title={organizacion.habilitado ? "Deshabilitar organización" : "Habilitar organización"}
                        description={organizacion.habilitado
                            ? `Se deshabilitará el ingreso de todos los usuarios de la organización "${organizacion.nombre}".`
                            : `Se habilitará el ingreso de todos los usuarios de la organización "${organizacion.nombre}".`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({ habilitado: !organizacion.habilitado });
                            notify(`Se ha ${organizacion.habilitado ? "deshabilitado" : "habilitado"} la organización.`, { variant: "success" });
                        }}
                    />}
                </SetOrganizacionHabilitadoMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {organizacion => <DeleteOrganizacionMutation id={organizacion.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Organización "${organizacion.nombre}"`}
                        description={`¿Estás seguro que quieres borrar la organización "${organizacion.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La organización "${organizacion.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteOrganizacionMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialogObs
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllOrganizaciones}
            defaultFileTitle="Usuarios"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllOrganizaciones}
            defaultFileTitle="Usuarios"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllOrganizaciones}
            defaultFileTitle="Usuarios"
        />
    </ListPage>
}